// src/components/StockViewer.js
import React, { useEffect, useState } from 'react';
import { db } from '../firebase';
import {
  collection,
  onSnapshot,
  doc,
  runTransaction,
} from 'firebase/firestore';

const SORT_OPTIONS = [
  { key: 'itemId', label: 'Item Code' },
  { key: 'description', label: 'Description' },
  { key: 'unitWeight', label: 'Unit Weight (Kg)' },
  { key: 'storeBoxes', label: 'Warehouse (Boxes)' },
  { key: 'storeKg', label: 'Warehouse (Kg)' },
  { key: 'shopBoxes', label: 'Shop (Boxes)' },
  { key: 'shopKg', label: 'Shop (Kg)' },
];

const emptyIngredientForm = {
  id: '',
  description: '',
  boxWeight: '',
  pricePerKg: '',
  unitsPerBox: '',
  weightPerUnit: '',
};

const StockViewer = () => {
  const [storeStock, setStoreStock] = useState([]);
  const [shopStock, setShopStock] = useState([]);
  const [sortConfig, setSortConfig] = useState({
    key: 'itemId',
    direction: 'asc',
  });

  const [showAddForm, setShowAddForm] = useState(false);
  const [ingredientForm, setIngredientForm] = useState(emptyIngredientForm);

  useEffect(() => {
    const unsubscribeStore = onSnapshot(
      collection(db, 'stock_store'),
      (snapshot) => {
        setStoreStock(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
      }
    );

    const unsubscribeShop = onSnapshot(
      collection(db, 'stock_shop'),
      (snapshot) => {
        setShopStock(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
      }
    );

    return () => {
      unsubscribeStore();
      unsubscribeShop();
    };
  }, []);

  const getItemData = (collectionArr, id) =>
    collectionArr.find((item) => item.id === id);

  const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;
  const fmt2 = (value) => Number(value ?? 0).toFixed(2);

  const calcBoxes = (item) => {
    const kg = Number(item?.totalKg ?? 0);
    const bw = Number(item?.boxWeight ?? 0);
    return bw > 0 ? kg / bw : 0;
  };

  const calcUnitWeight = (item) => {
    const bw = Number(item?.boxWeight ?? 0);
    const upb = Number(item?.unitsPerBox ?? item?.unitsperbox ?? 0);
    return bw > 0 && upb > 0 ? bw / upb : 0;
  };

  const adjustStock = async (collectionName, item, deltaBoxes) => {
    if (!item?.id) return;
    const ref = doc(db, collectionName, item.id);

    await runTransaction(db, async (tx) => {
      const snap = await tx.get(ref);
      if (!snap.exists()) return;

      const data = snap.data();
      const bw = Number(data.boxWeight ?? item.boxWeight ?? 0);
      if (bw <= 0) return;

      const currKg = Number(data.totalKg ?? 0);
      const deltaKg = bw * Number(deltaBoxes || 0);
      const newTotalKg = round2(currKg + deltaKg);

      if (newTotalKg < 0) {
        throw new Error(`Cannot reduce "${item.id}" below 0`);
      }

      const newBoxes = round2(newTotalKg / bw);

      tx.update(ref, {
        totalKg: newTotalKg,
        boxes: newBoxes,
      });
    });
  };

  const handleIngredientInputChange = (field, value) => {
    setIngredientForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const resetIngredientForm = () => {
    setIngredientForm(emptyIngredientForm);
    setShowAddForm(false);
  };

  const handleAddIngredient = async () => {
    const itemId = ingredientForm.id.trim().toUpperCase();
    const description = ingredientForm.description.trim();

    const boxWeight = Number(ingredientForm.boxWeight);
    const pricePerKg = Number(ingredientForm.pricePerKg);
    const unitsPerBox = Number(ingredientForm.unitsPerBox);
    const weightPerUnit = Number(ingredientForm.weightPerUnit);

    if (!itemId) return alert('⚠️ Item Code is required');
    if (!description) return alert('⚠️ Description is required');
    if (!boxWeight || boxWeight <= 0) return alert('⚠️ Box Weight must be greater than 0');
    if (!unitsPerBox || unitsPerBox <= 0) return alert('⚠️ Units Per Box must be greater than 0');
    if (!weightPerUnit || weightPerUnit <= 0) return alert('⚠️ Weight Per Unit must be greater than 0');
    if (pricePerKg < 0) return alert('⚠️ Price Per Kg cannot be negative');

    const basePayload = {
      id: itemId,
      description,
      boxWeight: round2(boxWeight),
      pricePerKg: round2(pricePerKg),
      unitsPerBox: round2(unitsPerBox),
      weightPerUnit: round2(weightPerUnit),
      totalKg: 0,
      boxes: 0,
    };

    const storeRef = doc(db, 'stock_store', itemId); // change to 'stock_storex' if needed
    const shopRef = doc(db, 'stock_shop', itemId);

    try {
      await runTransaction(db, async (tx) => {
        const [storeSnap, shopSnap] = await Promise.all([
          tx.get(storeRef),
          tx.get(shopRef),
        ]);

        if (storeSnap.exists() || shopSnap.exists()) {
          throw new Error(`Item "${itemId}" already exists in one of the collections`);
        }

        tx.set(storeRef, basePayload);
        tx.set(shopRef, basePayload);
      });

      alert(`✅ Ingredient "${itemId}" added to stock_store and stock_shop`);
      resetIngredientForm();
    } catch (e) {
      alert(`❌ ${e.message}`);
    }
  };

  const allItemIds = Array.from(
    new Set([...storeStock.map((i) => i.id), ...shopStock.map((i) => i.id)])
  );

  const rows = allItemIds.map((itemId) => {
    const storeItem = getItemData(storeStock, itemId) || {};
    const shopItem = getItemData(shopStock, itemId) || {};
    const description = storeItem.description || shopItem.description || '-';
    const configItem = storeItem.boxWeight ? storeItem : shopItem;

    const unitWeight = calcUnitWeight(configItem);
    const storeBoxes = calcBoxes(storeItem);
    const storeKg = Number(storeItem.totalKg ?? 0);
    const shopBoxes = calcBoxes(shopItem);
    const shopKg = Number(shopItem.totalKg ?? 0);

    return {
      itemId,
      storeItem,
      shopItem,
      configItem,
      description,
      unitWeight,
      storeBoxes,
      storeKg,
      shopBoxes,
      shopKg,
    };
  });

  const sortedRows = [...rows].sort((a, b) => {
    const { key, direction } = sortConfig;

    let aVal = a[key];
    let bVal = b[key];

    const isNumber = typeof aVal === 'number' && typeof bVal === 'number';

    if (!isNumber) {
      aVal = String(aVal ?? '').toLowerCase();
      bVal = String(bVal ?? '').toLowerCase();
    }

    let result = 0;
    if (aVal < bVal) result = -1;
    if (aVal > bVal) result = 1;

    return direction === 'asc' ? result : -result;
  });

  const requestSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return {
          key,
          direction: prev.direction === 'asc' ? 'desc' : 'asc',
        };
      }
      return { key, direction: 'asc' };
    });
  };

  const renderSortArrow = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === 'asc' ? ' ▲' : ' ▼';
  };

  const handleSortSelectChange = (e) => {
    const newKey = e.target.value;
    setSortConfig((prev) => ({
      ...prev,
      key: newKey,
    }));
  };

  const toggleDirection = () => {
    setSortConfig((prev) => ({
      ...prev,
      direction: prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '12px',
          flexWrap: 'wrap',
          marginBottom: '12px',
        }}
      >
        <h2 style={{ margin: 0 }}>📊 Real-Time Stock Viewer</h2>

        <button
          type="button"
          className="btn btn-primary"
          onClick={() => setShowAddForm((prev) => !prev)}
        >
          {showAddForm ? '✖ Close' : '➕ Add Ingredient'}
        </button>
      </div>

      {showAddForm && (
        <div
          style={{
            border: '1px solid #ccc',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '16px',
          }}
        >
          <h3 style={{ marginTop: 0 }}>New Ingredient</h3>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: '12px',
            }}
          >
            <input
              className="input"
              placeholder="Item Code"
              value={ingredientForm.id}
              onChange={(e) => handleIngredientInputChange('id', e.target.value)}
            />

            <input
              className="input"
              placeholder="Description"
              value={ingredientForm.description}
              onChange={(e) =>
                handleIngredientInputChange('description', e.target.value)
              }
            />

            <input
              className="input"
              type="number"
              min="0"
              step="0.01"
              placeholder="Box Weight (kg)"
              value={ingredientForm.boxWeight}
              onChange={(e) =>
                handleIngredientInputChange('boxWeight', e.target.value)
              }
            />

            <input
              className="input"
              type="number"
              min="0"
              step="0.01"
              placeholder="Price Per Kg"
              value={ingredientForm.pricePerKg}
              onChange={(e) =>
                handleIngredientInputChange('pricePerKg', e.target.value)
              }
            />

            <input
              className="input"
              type="number"
              min="0"
              step="1"
              placeholder="Units Per Box"
              value={ingredientForm.unitsPerBox}
              onChange={(e) =>
                handleIngredientInputChange('unitsPerBox', e.target.value)
              }
            />

            <input
              className="input"
              type="number"
              min="0"
              step="0.01"
              placeholder="Weight Per Unit"
              value={ingredientForm.weightPerUnit}
              onChange={(e) =>
                handleIngredientInputChange('weightPerUnit', e.target.value)
              }
            />
          </div>

          <div style={{ marginTop: '12px', display: 'flex', gap: '10px' }}>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleAddIngredient}
            >
              Save Ingredient
            </button>

            <button
              type="button"
              className="btn"
              onClick={resetIngredientForm}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="table-sort-controls">
        <label className="table-sort-label">
          Sort by
          <select
            className="table-sort-select"
            value={sortConfig.key}
            onChange={handleSortSelectChange}
          >
            {SORT_OPTIONS.map((opt) => (
              <option key={opt.key} value={opt.key}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>

        <button
          type="button"
          className="table-sort-direction"
          onClick={toggleDirection}
        >
          {sortConfig.direction === 'asc' ? '⬆︎ Asc' : '⬇︎ Desc'}
        </button>
      </div>

      <div className="table-responsive">
        <table
          className="table table--stack"
          border="1"
          cellPadding="8"
          style={{ textAlign: 'center', width: '100%' }}
        >
          <thead>
            <tr>
              <th
                style={{ minWidth: '97px', cursor: 'pointer' }}
                onClick={() => requestSort('itemId')}
              >
                Item Code{renderSortArrow('itemId')}
              </th>
              <th
                style={{ cursor: 'pointer' }}
                onClick={() => requestSort('description')}
              >
                Description{renderSortArrow('description')}
              </th>
              <th
                style={{ cursor: 'pointer' }}
                onClick={() => requestSort('unitWeight')}
              >
                Unit Weight (Kg){renderSortArrow('unitWeight')}
              </th>
              <th
                style={{ cursor: 'pointer' }}
                onClick={() => requestSort('storeBoxes')}
              >
                Warehouse (Boxes){renderSortArrow('storeBoxes')}
              </th>
              <th
                style={{ cursor: 'pointer' }}
                onClick={() => requestSort('storeKg')}
              >
                Warehouse (Kg){renderSortArrow('storeKg')}
              </th>
              <th
                style={{ cursor: 'pointer' }}
                onClick={() => requestSort('shopBoxes')}
              >
                Shop (Boxes){renderSortArrow('shopBoxes')}
              </th>
              <th
                style={{ cursor: 'pointer' }}
                onClick={() => requestSort('shopKg')}
              >
                Shop (Kg){renderSortArrow('shopKg')}
              </th>
            </tr>
          </thead>

          <tbody>
            {sortedRows.map((row) => (
              <tr key={row.itemId}>
                <td data-label="Item Code">{row.itemId}</td>
                <td data-label="Description">{row.description}</td>

                <td data-label="Unit Weight (Kg)">
                  {fmt2(row.unitWeight)}
                </td>

                <td data-label="Warehouse (Boxes)">
                  <div className="qty-actions">
                    <span className="qty">{fmt2(row.storeBoxes)}</span>
                    <div className="actions-inline">
                      <button
                        onClick={() =>
                          adjustStock('stock_store', row.storeItem, 1)
                        }
                      >
                        ➕
                      </button>
                      <button
                        onClick={() =>
                          adjustStock('stock_store', row.storeItem, -1)
                        }
                      >
                        ➖
                      </button>
                    </div>
                  </div>
                </td>

                <td data-label="Warehouse (Kg)">
                  <span className="qty">{fmt2(row.storeKg)}</span>
                </td>

                <td data-label="Shop (Boxes)">
                  <div className="qty-actions">
                    <span className="qty">{fmt2(row.shopBoxes)}</span>
                    <div className="actions-inline">
                      <button
                        onClick={() =>
                          adjustStock('stock_shop', row.shopItem, 1)
                        }
                      >
                        ➕
                      </button>
                      <button
                        onClick={() =>
                          adjustStock('stock_shop', row.shopItem, -1)
                        }
                      >
                        ➖
                      </button>
                    </div>
                  </div>
                </td>

                <td data-label="Shop (Kg)">
                  <span className="qty">{fmt2(row.shopKg)}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p style={{ marginTop: '8px', fontSize: '0.9rem' }}>
        Tip: choose <strong>Shop (Boxes)</strong> above to sort the cards by
        total boxes in the shop.
      </p>
    </div>
  );
};

export default StockViewer;